import {
  Avatar, Box, Button, Card, CardContent, Chip, Divider, Grid, Stack, Typography,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import VideocamIcon from '@mui/icons-material/Videocam';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import ChatIcon from '@mui/icons-material/Chat';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import CancelIcon from '@mui/icons-material/Cancel';
import LoginIcon from '@mui/icons-material/Login';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { TriageBadge } from '@/components/SeverityChip';
import { useApiQuery, useApiMutation } from '@/hooks/useApiQuery';
import { useToast } from '@/components/ToastProvider';
import { patient } from '@/api/endpoints';
import { formatDateTime, formatPrice } from '@/utils/format';
import type { AppointmentDetail, Modality, TriageLevel } from '@/types/medical';

const CANCELLABLE = new Set<AppointmentDetail['status']>([
  'booked', 'confirmed', 'checked_in',
]);
const CHECK_INABLE = new Set<AppointmentDetail['status']>([
  'booked', 'confirmed',
]);

export function PatientAppointmentDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const [cancelOpen, setCancelOpen] = useState(false);

  const q = useApiQuery({
    queryKey: ['patient', 'appointments', id],
    queryFn:  () => patient.appointments.get(id),
    enabled:  Boolean(id),
  });

  const cancelMut = useApiMutation({
    mutationFn: (reason: string) => patient.appointments.cancel(id, reason),
    onSuccess: () => {
      toast.success('Appointment cancelled.');
      qc.invalidateQueries({ queryKey: ['patient', 'appointments'] });
      setCancelOpen(false);
    },
    onError: (e) => toast.error(e.message),
  });

  const checkInMut = useApiMutation({
    mutationFn: () => patient.appointments.checkIn(id),
    onSuccess: (data) => {
      toast.success('Checked in. Stand by — your clinician will start the visit shortly.');
      qc.invalidateQueries({ queryKey: ['patient', 'appointments', id] });
      if (data.video_room_id) {
        // We don't auto-navigate; the consult is launched from the detail card
      }
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <>
      <PageHeader
        title="Appointment"
        breadcrumbs={[
          { label: 'My appointments', to: '/patient/appointments' },
          { label: 'Detail' },
        ]}
      />

      <DataState query={q}>
        {(appt) => (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <PrimaryCard appt={appt}
                onCheckIn={() => checkInMut.mutate()}
                checkInBusy={checkInMut.isPending}
                onCancel={() => setCancelOpen(true)}
              />
              {appt.chief_complaint && <ChiefComplaintCard appt={appt} />}
              {appt.notes_for_patient && <NotesCard appt={appt} />}
            </Grid>
            <Grid item xs={12} md={4}>
              <ClinicianCard appt={appt} onView={() => nav(`/clinicians/${appt.clinician?.id}`)} />
              <BillingCard appt={appt} />
              {appt.ai_triage_level && <TriageCard level={appt.ai_triage_level} />}
            </Grid>
          </Grid>
        )}
      </DataState>

      <ConfirmDialog
        open={cancelOpen}
        title="Cancel appointment?"
        description="Cancellation policies depend on the practice. Late cancellations may incur a fee."
        confirmLabel="Cancel appointment"
        cancelLabel="Keep it"
        destructive
        reasonRequired
        reasonLabel="Reason (helps the clinician keep records)"
        onCancel={() => setCancelOpen(false)}
        onConfirm={(reason) => cancelMut.mutateAsync(reason ?? '')}
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function PrimaryCard({
  appt, onCheckIn, checkInBusy, onCancel,
}: {
  appt: AppointmentDetail;
  onCheckIn: () => void;
  checkInBusy: boolean;
  onCancel: () => void;
}) {
  const canCancel = CANCELLABLE.has(appt.status);
  const canCheckIn = CHECK_INABLE.has(appt.status);
  const inConsult = appt.status === 'in_consult';

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <ModalityBlock modality={appt.modality} large />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" component="h2" sx={{ mb: 0.5 }}>
              {appt.procedure?.name ?? 'Appointment'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatDateTime(appt.start_at)} – {formatDateTime(appt.end_at)}
              {appt.timezone_name && ` (${appt.timezone_name})`}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {(canCheckIn || inConsult) && appt.modality !== 'in_person' && (
              <Button
                variant="contained"
                startIcon={<LoginIcon />}
                onClick={onCheckIn}
                disabled={checkInBusy || inConsult}
              >
                {inConsult ? 'Join consult' : 'Check in'}
              </Button>
            )}
            {canCancel && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={onCancel}
              >
                Cancel
              </Button>
            )}
          </Stack>
        </Stack>

        {appt.video_room_id && (appt.status === 'checked_in' || inConsult) && (
          <Box
            sx={{
              mt: 3, p: 2, bgcolor: 'background.subtle',
              borderRadius: 2, display: 'flex', justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography variant="subtitle2">Video consult ready</Typography>
              <Typography variant="body2" color="text.secondary">
                Room: <span className="tabular">{appt.video_room_id}</span>
              </Typography>
            </Box>
            <Button variant="contained" href={`/medical/video/${appt.video_room_id}/`}>
              Enter room
            </Button>
          </Box>
        )}

        {appt.cancelled_at && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Cancelled {formatDateTime(appt.cancelled_at)}
              {appt.cancellation_reason && <> — “{appt.cancellation_reason}”</>}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function ChiefComplaintCard({ appt }: { appt: AppointmentDetail }) {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">In your words</Typography>
        <Typography variant="body1" sx={{ mt: 0.5 }}>
          {appt.chief_complaint}
        </Typography>
      </CardContent>
    </Card>
  );
}

function NotesCard({ appt }: { appt: AppointmentDetail }) {
  return (
    <Card>
      <CardContent>
        <Typography variant="overline" color="text.secondary">Notes for you</Typography>
        <Typography variant="body1" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
          {appt.notes_for_patient}
        </Typography>
        {appt.follow_up_recommended_in_days != null && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2">
              <strong>Follow-up recommended</strong> in about{' '}
              {appt.follow_up_recommended_in_days} day(s).
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ClinicianCard({ appt, onView }: { appt: AppointmentDetail; onView: () => void }) {
  const c = appt.clinician;
  if (!c) return null;
  const initials = (c.display_name || '?')
    .split(' ').map((p) => p[0] ?? '').join('').slice(0, 2);
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack direction="row" spacing={2}>
          <Avatar
            src={c.photo ?? undefined}
            sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}
          >
            {initials}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600} noWrap>
              {c.display_name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {c.specialties?.map((s) => s.name).join(' · ') || '—'}
            </Typography>
          </Box>
        </Stack>
        <Box sx={{ mt: 2 }}>
          <Button size="small" onClick={onView} variant="text">
            View profile
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

function BillingCard({ appt }: { appt: AppointmentDetail }) {
  const owed = parseFloat(appt.patient_owed_amount || '0');
  const total = parseFloat(appt.price_amount || '0');
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">Billing</Typography>
        <Stack spacing={1} sx={{ mt: 1 }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2">Total</Typography>
            <Typography variant="body2" className="tabular" fontWeight={600}>
              {formatPrice(total, appt.price_currency)}
            </Typography>
          </Stack>
          {parseFloat(appt.insurer_covered_amount || '0') > 0 && (
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Insurer covers</Typography>
              <Typography variant="body2" className="tabular" color="text.secondary">
                −{formatPrice(parseFloat(appt.insurer_covered_amount), appt.price_currency)}
              </Typography>
            </Stack>
          )}
          {owed > 0 && (
            <>
              <Divider />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">Your share</Typography>
                <Typography variant="body2" className="tabular" fontWeight={700}>
                  {formatPrice(owed, appt.price_currency)}
                </Typography>
              </Stack>
            </>
          )}
          <Chip
            size="small"
            label={appt.payment_status}
            sx={{ alignSelf: 'flex-start', mt: 1 }}
            color={appt.payment_status === 'paid' ? 'success'
                   : appt.payment_status === 'unpaid' ? 'warning'
                   : 'default'}
            variant="outlined"
          />
        </Stack>
      </CardContent>
    </Card>
  );
}

function TriageCard({ level }: { level: TriageLevel }) {
  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2}>
          <TriageBadge level={level} />
          <Box>
            <Typography variant="body2">
              Triage level set automatically based on your intake form.
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function ModalityBlock({ modality, large }: { modality: Modality; large?: boolean }) {
  const size = large ? 56 : 40;
  const icon = {
    in_person: <LocationOnIcon fontSize={large ? 'large' : 'medium'} />,
    video:     <VideocamIcon fontSize={large ? 'large' : 'medium'} />,
    phone:     <PhoneIcon fontSize={large ? 'large' : 'medium'} />,
    async:     <ChatIcon fontSize={large ? 'large' : 'medium'} />,
    home:      <HomeWorkIcon fontSize={large ? 'large' : 'medium'} />,
    hybrid:    <VideocamIcon fontSize={large ? 'large' : 'medium'} />,
  }[modality];
  const labels: Record<Modality, string> = {
    in_person: 'In person',
    video:     'Video',
    phone:     'Phone',
    async:     'Async chat',
    home:      'Home visit',
    hybrid:    'Hybrid',
  };
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Box
        sx={{
          width: size, height: size,
          borderRadius: 2, bgcolor: 'background.subtle',
          color: 'primary.main',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          mx: 'auto',
        }}
      >
        {icon}
      </Box>
      <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
        {labels[modality]}
      </Typography>
    </Box>
  );
}
